/**
 * Bulk Import Service
 * Handles importing accounts and related data from JSON files
 * Implements CREATE ONLY mode - skips existing accounts by name
 */
import { supabase as supabaseClient } from '@/lib/supabase-client';
const supabase = supabaseClient;
// CONSTANTS
const BATCH_SIZE = 50;
const SKIP_EXISTING = true; // CREATE ONLY mode
/**
 * Validate account data structure
 */
export function validateAccountData(account) {
    const errors = [];
    if (!account.name || typeof account.name !== 'string') {
        errors.push('Account name is required and must be a string');
    }
    if (account.type && typeof account.type !== 'string') {
        errors.push('Account type must be a string');
    }
    if (account.currency && !['USD', 'INR', 'GBP', 'EUR'].includes(account.currency)) {
        errors.push(`Invalid currency: ${account.currency}`);
    }
    if (account.balance !== undefined && typeof account.balance !== 'number') {
        errors.push('Balance must be a number');
    }
    return {
        valid: errors.length === 0,
        errors,
    };
}
/**
 * Check if account already exists by name (case-insensitive)
 */
async function checkAccountExists(organizationId, accountName) {
    try {
        const { data, error } = await supabase
            .from('accounts')
            .select('id')
            .eq('organization_id', organizationId)
            .ilike('name', accountName)
            .limit(1);
        if (error) {
            console.error('Error checking account existence:', error);
            return false;
        }
        return data && data.length > 0;
    }
    catch (error) {
        console.error('Error in checkAccountExists:', error);
        return false;
    }
}
/**
 * Create a single account
 */
async function createAccount(organizationId, userId, accountData) {
    try {
        // Check if account already exists
        if (SKIP_EXISTING) {
            const exists = await checkAccountExists(organizationId, accountData.name);
            if (exists) {
                return {
                    success: false,
                    error: `Account '${accountData.name}' already exists (CREATE ONLY mode)`,
                };
            }
        }
        // Validate
        const validation = validateAccountData(accountData);
        if (!validation.valid) {
            return {
                success: false,
                error: `Validation failed: ${validation.errors.join(', ')}`,
            };
        }
        // Create account
        const { data, error } = await supabase
            .from('accounts')
            .insert([
            {
                organization_id: organizationId,
                name: accountData.name,
                type: accountData.type || 'other',
                balance: accountData.balance || 0,
                currency: accountData.currency || 'USD',
                is_active: accountData.isActive !== false,
                created_by: userId,
            },
        ])
            .select('id');
        if (error) {
            return {
                success: false,
                error: `Database error: ${error.message}`,
            };
        }
        if (!data || data.length === 0) {
            return {
                success: false,
                error: 'No data returned from insert',
            };
        }
        return {
            success: true,
            accountId: data[0].id,
        };
    }
    catch (error) {
        return {
            success: false,
            error: `Exception: ${error.message}`,
        };
    }
}
/**
 * Create financial details for an account
 */
async function createFinancialDetail(organizationId, userId, detail) {
    try {
        const { error } = await supabase
            .from('account_financial_details')
            .insert([
            {
                organization_id: organizationId,
                account_id: detail.accountId,
                financial_status: detail.financialStatus || 'not_budgeted',
                next_payable_date: detail.nextPayableDate || null,
                next_payment_usd: detail.nextPaymentUSD || null,
                next_payment_inr: detail.nextPaymentINR || null,
                payable_period_start: detail.payablePeriodStart || null,
                payable_period_end: detail.payablePeriodEnd || null,
                total_balance_usd: detail.totalBalanceUSD || null,
                total_balance_inr: detail.totalBalanceINR || null,
                usd_units: detail.usdUnits || null,
                inr_units: detail.inrUnits || null,
                created_by: userId,
            },
        ]);
        if (error) {
            console.error('Financial detail insert error:', error);
            return false;
        }
        return true;
    }
    catch (error) {
        console.error('Error creating financial detail:', error);
        return false;
    }
}
/**
 * Create billing schedule for an account
 */
async function createBillingSchedule(organizationId, userId, schedule) {
    try {
        const { error } = await supabase
            .from('account_billing_schedules')
            .insert([
            {
                organization_id: organizationId,
                account_id: schedule.accountId,
                billing_interval: schedule.billingInterval || 'monthly',
                priority: schedule.priority || 'bare_minimum',
                urgency: schedule.urgency || 'unnecessary',
                monday_doc_reference: schedule.mondayDocReference || null,
                created_by: userId,
            },
        ]);
        if (error) {
            console.error('Billing schedule insert error:', error);
            return false;
        }
        return true;
    }
    catch (error) {
        console.error('Error creating billing schedule:', error);
        return false;
    }
}
/**
 * Create categorization for an account
 */
async function createCategorization(organizationId, userId, categorization) {
    try {
        const { error } = await supabase
            .from('account_categorizations')
            .insert([
            {
                organization_id: organizationId,
                account_id: categorization.accountId,
                category: categorization.category || 'Uncategorized',
                sub_category: categorization.subCategory || null,
                account_owner_group: categorization.accountOwnerGroup || 'Unassigned',
                account_type_category: categorization.accountTypeCategory || 'uncategorized',
                created_by: userId,
            },
        ]);
        if (error) {
            console.error('Categorization insert error:', error);
            return false;
        }
        return true;
    }
    catch (error) {
        console.error('Error creating categorization:', error);
        return false;
    }
}
/**
 * Create time tracking entry for an account
 */
async function createTimeTrackingEntry(organizationId, userId, entry) {
    try {
        const { error } = await supabase
            .from('time_tracking_entries')
            .insert([
            {
                organization_id: organizationId,
                account_id: entry.accountId || null,
                project_name: entry.projectName || null,
                started_by: entry.startedBy || 'Unknown',
                ended_by: entry.endedBy || null,
                start_date: entry.startDate,
                end_date: entry.endDate,
                start_time: entry.startTime || null,
                end_time: entry.endTime || null,
                duration: entry.duration || '00:00:00',
                notes: entry.notes || null,
                created_by: userId,
            },
        ]);
        if (error) {
            console.error('Time tracking insert error:', error);
            return false;
        }
        return true;
    }
    catch (error) {
        console.error('Error creating time tracking entry:', error);
        return false;
    }
}
/**
 * Import accounts from JSON data
 */
export async function importAccountsFromJSON(organizationId, userId, accountsData, onProgress) {
    const result = {
        success: true,
        totalRecords: accountsData.length,
        importedRecords: 0,
        skippedRecords: 0,
        failedRecords: 0,
        errors: [],
        details: {
            accounts: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            financialDetails: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            billingSchedules: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            categorizations: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            timeTracking: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
        },
    };
    // Map to track account IDs by name
    const accountMap = new Map();
    // Process in batches
    for (let i = 0; i < accountsData.length; i += BATCH_SIZE) {
        const batch = accountsData.slice(i, Math.min(i + BATCH_SIZE, accountsData.length));
        for (const accountData of batch) {
            const index = accountsData.indexOf(accountData);
            // Report progress
            if (onProgress) {
                onProgress({
                    currentStep: `Importing account: ${accountData.name || 'Unknown'}`,
                    currentIndex: index,
                    totalRecords: accountsData.length,
                    percentage: Math.round((index / accountsData.length) * 100),
                });
            }
            result.details.accounts.attempted++;
            // Create account
            const accountResult = await createAccount(organizationId, userId, accountData);
            if (!accountResult.success) {
                if (accountResult.error?.includes('already exists')) {
                    result.skippedRecords++;
                    result.details.accounts.skipped++;
                }
                else {
                    result.failedRecords++;
                    result.details.accounts.failed++;
                    result.errors.push(`Account '${accountData.name}': ${accountResult.error}`);
                }
                continue;
            }
            const accountId = accountResult.accountId;
            accountMap.set(accountData.name, accountId);
            result.importedRecords++;
            result.details.accounts.imported++;
        }
    }
    return result;
}
/**
 * Import financial details from JSON data
 */
export async function importFinancialDetailsFromJSON(organizationId, userId, accountMap, financialData, onProgress) {
    const result = {
        success: true,
        totalRecords: financialData.length,
        importedRecords: 0,
        skippedRecords: 0,
        failedRecords: 0,
        errors: [],
        details: {
            accounts: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            financialDetails: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            billingSchedules: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            categorizations: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            timeTracking: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
        },
    };
    for (let i = 0; i < financialData.length; i++) {
        const detail = financialData[i];
        if (onProgress) {
            onProgress({
                currentStep: `Importing financial detail ${i + 1}/${financialData.length}`,
                currentIndex: i,
                totalRecords: financialData.length,
                percentage: Math.round(((i + 1) / financialData.length) * 100),
            });
        }
        result.details.financialDetails.attempted++;
        // Match account ID from original UUID or by name mapping
        let accountId = detail.accountId;
        if (!accountId && detail.accountName) {
            accountId = accountMap.get(detail.accountName);
        }
        if (!accountId) {
            result.failedRecords++;
            result.details.financialDetails.failed++;
            result.errors.push(`Financial detail: No matching account found`);
            continue;
        }
        const success = await createFinancialDetail(organizationId, userId, {
            ...detail,
            accountId,
        });
        if (success) {
            result.importedRecords++;
            result.details.financialDetails.imported++;
        }
        else {
            result.failedRecords++;
            result.details.financialDetails.failed++;
            result.errors.push(`Financial detail for account ${accountId}: Failed to insert`);
        }
    }
    return result;
}
/**
 * Import billing schedules from JSON data
 */
export async function importBillingSchedulesFromJSON(organizationId, userId, accountMap, scheduleData, onProgress) {
    const result = {
        success: true,
        totalRecords: scheduleData.length,
        importedRecords: 0,
        skippedRecords: 0,
        failedRecords: 0,
        errors: [],
        details: {
            accounts: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            financialDetails: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            billingSchedules: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            categorizations: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            timeTracking: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
        },
    };
    for (let i = 0; i < scheduleData.length; i++) {
        const schedule = scheduleData[i];
        if (onProgress) {
            onProgress({
                currentStep: `Importing billing schedule ${i + 1}/${scheduleData.length}`,
                currentIndex: i,
                totalRecords: scheduleData.length,
                percentage: Math.round(((i + 1) / scheduleData.length) * 100),
            });
        }
        result.details.billingSchedules.attempted++;
        let accountId = schedule.accountId;
        if (!accountId && schedule.accountName) {
            accountId = accountMap.get(schedule.accountName);
        }
        if (!accountId) {
            result.failedRecords++;
            result.details.billingSchedules.failed++;
            result.errors.push(`Billing schedule: No matching account found`);
            continue;
        }
        const success = await createBillingSchedule(organizationId, userId, {
            ...schedule,
            accountId,
        });
        if (success) {
            result.importedRecords++;
            result.details.billingSchedules.imported++;
        }
        else {
            result.failedRecords++;
            result.details.billingSchedules.failed++;
            result.errors.push(`Billing schedule for account ${accountId}: Failed to insert`);
        }
    }
    return result;
}
/**
 * Import categorizations from JSON data
 */
export async function importCategorizationsFromJSON(organizationId, userId, accountMap, categorizationData, onProgress) {
    const result = {
        success: true,
        totalRecords: categorizationData.length,
        importedRecords: 0,
        skippedRecords: 0,
        failedRecords: 0,
        errors: [],
        details: {
            accounts: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            financialDetails: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            billingSchedules: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            categorizations: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            timeTracking: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
        },
    };
    for (let i = 0; i < categorizationData.length; i++) {
        const categorization = categorizationData[i];
        if (onProgress) {
            onProgress({
                currentStep: `Importing categorization ${i + 1}/${categorizationData.length}`,
                currentIndex: i,
                totalRecords: categorizationData.length,
                percentage: Math.round(((i + 1) / categorizationData.length) * 100),
            });
        }
        result.details.categorizations.attempted++;
        let accountId = categorization.accountId;
        if (!accountId && categorization.accountName) {
            accountId = accountMap.get(categorization.accountName);
        }
        if (!accountId) {
            result.failedRecords++;
            result.details.categorizations.failed++;
            result.errors.push(`Categorization: No matching account found`);
            continue;
        }
        const success = await createCategorization(organizationId, userId, {
            ...categorization,
            accountId,
        });
        if (success) {
            result.importedRecords++;
            result.details.categorizations.imported++;
        }
        else {
            result.failedRecords++;
            result.details.categorizations.failed++;
            result.errors.push(`Categorization for account ${accountId}: Failed to insert`);
        }
    }
    return result;
}
/**
 * Import time tracking entries from JSON data
 */
export async function importTimeTrackingFromJSON(organizationId, userId, accountMap, timeTrackingData, onProgress) {
    const result = {
        success: true,
        totalRecords: timeTrackingData.length,
        importedRecords: 0,
        skippedRecords: 0,
        failedRecords: 0,
        errors: [],
        details: {
            accounts: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            financialDetails: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            billingSchedules: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            categorizations: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
            timeTracking: { attempted: 0, imported: 0, skipped: 0, failed: 0 },
        },
    };
    for (let i = 0; i < timeTrackingData.length; i++) {
        const entry = timeTrackingData[i];
        if (onProgress) {
            onProgress({
                currentStep: `Importing time entry ${i + 1}/${timeTrackingData.length}`,
                currentIndex: i,
                totalRecords: timeTrackingData.length,
                percentage: Math.round(((i + 1) / timeTrackingData.length) * 100),
            });
        }
        result.details.timeTracking.attempted++;
        let accountId = entry.accountId;
        if (!accountId && entry.accountName) {
            accountId = accountMap.get(entry.accountName);
        }
        const success = await createTimeTrackingEntry(organizationId, userId, {
            ...entry,
            accountId: accountId || null,
        });
        if (success) {
            result.importedRecords++;
            result.details.timeTracking.imported++;
        }
        else {
            result.failedRecords++;
            result.details.timeTracking.failed++;
            result.errors.push(`Time tracking entry: Failed to insert`);
        }
    }
    return result;
}
/**
 * Import master account file (combined data)
 */
export async function importMasterFileFromJSON(organizationId, userId, masterData, onProgress) {
    const allResults = [];
    const accountMap = new Map();
    // Step 1: Import accounts
    const accountsResult = await importAccountsFromJSON(organizationId, userId, masterData.map((item) => ({
        id: item.id,
        name: item.name,
        type: item.type,
        currency: item.currency,
        balance: item.balance,
        isActive: item.isActive,
    })), onProgress);
    allResults.push(accountsResult);
    // Build account map from imported accounts
    for (const item of masterData) {
        if (!accountMap.has(item.name)) {
            // The account IDs are already UUID, so use them directly
            accountMap.set(item.name, item.id);
        }
    }
    // Step 2: Import financial details
    const financialDetails = masterData
        .filter((item) => item.financialDetails)
        .map((item) => ({
        accountId: item.id,
        accountName: item.name,
        ...item.financialDetails,
    }));
    if (financialDetails.length > 0) {
        const financialResult = await importFinancialDetailsFromJSON(organizationId, userId, accountMap, financialDetails, onProgress);
        allResults.push(financialResult);
    }
    // Step 3: Import billing schedules
    const billingSchedules = masterData
        .filter((item) => item.billingSchedule)
        .map((item) => ({
        accountId: item.id,
        accountName: item.name,
        ...item.billingSchedule,
    }));
    if (billingSchedules.length > 0) {
        const billingResult = await importBillingSchedulesFromJSON(organizationId, userId, accountMap, billingSchedules, onProgress);
        allResults.push(billingResult);
    }
    // Step 4: Import categorizations
    const categorizations = masterData
        .filter((item) => item.categorization)
        .map((item) => ({
        accountId: item.id,
        accountName: item.name,
        ...item.categorization,
    }));
    if (categorizations.length > 0) {
        const categorizationResult = await importCategorizationsFromJSON(organizationId, userId, accountMap, categorizations, onProgress);
        allResults.push(categorizationResult);
    }
    // Step 5: Import time tracking
    const timeEntries = masterData
        .filter((item) => item.timeTrackingEntries && item.timeTrackingEntries.length > 0)
        .flatMap((item) => item.timeTrackingEntries.map((entry) => ({
        accountId: item.id,
        accountName: item.name,
        ...entry,
    })));
    if (timeEntries.length > 0) {
        const timeResult = await importTimeTrackingFromJSON(organizationId, userId, accountMap, timeEntries, onProgress);
        allResults.push(timeResult);
    }
    // Combine results
    const finalResult = {
        success: allResults.every((r) => r.success),
        totalRecords: allResults.reduce((sum, r) => sum + r.totalRecords, 0),
        importedRecords: allResults.reduce((sum, r) => sum + r.importedRecords, 0),
        skippedRecords: allResults.reduce((sum, r) => sum + r.skippedRecords, 0),
        failedRecords: allResults.reduce((sum, r) => sum + r.failedRecords, 0),
        errors: allResults.flatMap((r) => r.errors),
        details: {
            accounts: accountsResult.details.accounts,
            financialDetails: allResults.find((r) => r.details.financialDetails.attempted > 0)?.details.financialDetails || {
                attempted: 0,
                imported: 0,
                skipped: 0,
                failed: 0,
            },
            billingSchedules: allResults.find((r) => r.details.billingSchedules.attempted > 0)?.details.billingSchedules || {
                attempted: 0,
                imported: 0,
                skipped: 0,
                failed: 0,
            },
            categorizations: allResults.find((r) => r.details.categorizations.attempted > 0)?.details.categorizations || {
                attempted: 0,
                imported: 0,
                skipped: 0,
                failed: 0,
            },
            timeTracking: allResults.find((r) => r.details.timeTracking.attempted > 0)?.details.timeTracking || {
                attempted: 0,
                imported: 0,
                skipped: 0,
                failed: 0,
            },
        },
    };
    return finalResult;
}
